import type { DrawContext } from './draw';
import type { View } from './project';
import { toScreen } from './project';
import type { LabelSpace } from './labels';

/*
 * THE WEDGE LABELS — what each direction of the map means, said out loud.
 *
 * Ported from `reference/universe-v8/engine.js` — `drawStatic` lines 1643–1670.
 *
 * Angle means which texts name a figure, and that is the half of the map's claim a reader cannot
 * possibly guess. So once they are far enough out to see whole sectors at a time, each sector says
 * its own name and how many figures only it holds. Close in the labels go, because by then the
 * reader is looking at names rather than at directions.
 *
 * THE LABEL LOOKS FOR SOMEWHERE TO STAND, and their comment says why: *"the label may swing off its
 * own axis as well as pull in: the Mishnah petal points straight down into the corpus filter bar,
 * and radius alone never clears it"*. So it tries four distances along its own spoke and, at each,
 * five angles either side — twenty positions — and takes the first that is on screen, clear of the
 * chrome, and not already taken by another label. If none works the label is simply not drawn: a
 * wedge name overlapping the filter bar is worse than a wedge with no name.
 *
 * THAT ORDER IS THE PRIORITY. Pulling in is tried before swinging aside at every distance, because a
 * label a little closer to the middle still points at its own wedge, while one swung far off its
 * axis is pointing at somebody else's.
 */

/** One wedge, as this layer needs it. Every field of it is data. */
export interface Wedge {
  /** Its corpus key, which colours the label. */
  k: string;
  /** What it is called — theirs are English literals, ours come from the dictionary. */
  label: string;
  /** The angle its spoke points along, in radians. */
  a: number;
  /** How far out the label sits, in world units. */
  r: number;
  /** How many figures only this wedge holds. */
  count: number;
  /**
   * And how many it holds AT ALL, shared ones included.
   *
   * Both, because one alone is misread. The label said "11 named here only" under the Quran and
   * Ariel — who built the library — read it as the Quran having eleven names. It has 115 across
   * 4,304 passages; eleven is how many NO OTHER TEXT names, which on a map of shared scripture is
   * the more interesting number and the easier one to mistake for a total.
   */
  held?: number;
}

/**
 * The zooms the labels live between — arguments, because a zoom is pixels per world unit.
 *
 * The tenth and eleventh this port has lifted out.
 */
export interface WedgeZooms {
  /** Above this the labels are gone entirely. */
  until: number;
  /** And they fade in over this much zoom below it. */
  span: number;
}

/**
 * THEIRS: engine.js:1643 gates the petal labels at `z<0.13` and fades them over `(0.13-z)/0.04`.
 *
 * Exported for the same reason as the stars above. The test held these; the scene wired 0.2 and
 * 0.12, which are neither of them — and a gate two thirds wider than theirs keeps the labels on
 * through a range of zoom where their map has none.
 */
export const THEIR_WEDGE_ZOOMS: WedgeZooms = { until: 0.13, span: 0.04 };

/** What to draw with. The wedges, the palette and the phrase are all the caller's. */
export interface WedgePaint {
  wedges: readonly Wedge[];
  palette: Readonly<Record<string, string>>;
  /**
   * How to say "N named here only".
   *
   * A function rather than a format string, because Hebrew and Arabic do not put the number where
   * English does and neither pluralises the way English does. `web/src/i18n/strings.ts` exists to
   * stop exactly the English literal theirs has here.
   */
  countLine: (of: { held?: number; alone: number }) => string;
  /** The world's vertical scale. */
  squash: number;
}

/** How far out along its own spoke a label will pull, in search of room. */
const PULL_IN = [1, 0.86, 0.72, 0.6];

/** And how far off its own axis it will swing at each of those, in radians. */
const SWING = [0, 0.34, -0.34, 0.6, -0.6];

/** The fonts. Look. */
const LABEL_FONT = '500 12px "Space Grotesk", sans-serif';
const COUNT_FONT = '400 9.5px "Space Grotesk", sans-serif';

/** How much air a label wants either side of itself. */
const SIDE_PAD = 16;

/** How close to the edges of the screen a label may sit. */
const EDGE_X = 8;
const EDGE_Y = 26;

/** The box the chrome must not overlap, and the slightly larger one the label claims. */
const BLOCK_TOP = 13;
const BLOCK_BOTTOM = 22;
const CLAIM_TOP = 14;
const CLAIM_BOTTOM = 23;

/** How opaque the label is at full fade, of 255, and how opaque its count line is. */
const LABEL_ALPHA = 200;
const COUNT_ALPHA = 0.5;

/** How far under the label its count line sits. */
const COUNT_DROP = 15;

/** Which phases show the wedge names at all. */
const PHASES = ['map', 'survey', 'named'];

/** Whether this phase and zoom show them. */
export function wedgesShowing(phase: string, zoom: number, zooms: WedgeZooms): boolean {
  return zoom < zooms.until && PHASES.includes(phase);
}

/** How far in they are: nothing at the top of the range, all of it a little below. */
export function wedgeFade(zoom: number, zooms: WedgeZooms): number {
  return Math.min(1, (zooms.until - zoom) / zooms.span);
}

/** Somewhere a label can stand: on screen, clear of the chrome, and not already taken. */
export interface WedgeSpot {
  x: number;
  y: number;
}

/**
 * Look for room, nearest-to-its-own-meaning first.
 *
 * Returns null when there is none — and the label is then not drawn at all, which is the right
 * answer: a wedge name sitting on top of the filter bar says something false about both.
 */
export function findWedgeSpot(
  wedge: Wedge,
  view: View,
  squash: number,
  half: number,
  space: LabelSpace,
): WedgeSpot | null {
  const W = view.view.width;
  const H = view.view.height;
  for (const pull of PULL_IN) {
    for (const swing of SWING) {
      const angle = wedge.a + swing;
      const c = toScreen(view, Math.cos(angle) * wedge.r * pull, Math.sin(angle) * wedge.r * pull * squash);
      if (c.x - half < EDGE_X || c.x + half > W - EDGE_X || c.y < EDGE_Y || c.y > H - EDGE_Y) continue;
      if (space.blocked({ x0: c.x - half, y0: c.y - BLOCK_TOP, x1: c.x + half, y1: c.y + BLOCK_BOTTOM })) continue;
      /* ASKING IS TAKING: `claim` reserves the box when it succeeds, so the next wedge cannot land
         on top of this one. That is why the search must run in the order the labels are drawn. */
      if (!space.claim({ x0: c.x - half, y0: c.y - CLAIM_TOP, x1: c.x + half, y1: c.y + CLAIM_BOTTOM })) continue;
      return c;
    }
  }
  return null;
}

/** An alpha as the two hex digits their colours carry as a suffix. */
function hex(v: number): string {
  return Math.round(v).toString(16).padStart(2, '0');
}

/**
 * Draw the wedge names.
 *
 * `space` is shared with the node labels, which are drawn after: a wedge name is the more important
 * of the two, so it claims its room first and a figure's name gives way to it.
 */
export function drawWedges(ctx: DrawContext, view: View, paint: WedgePaint, zooms: WedgeZooms, space: LabelSpace): number {
  const fade = wedgeFade(view.cam.z, zooms);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  let written = 0;
  for (const wedge of paint.wedges) {
    ctx.font = LABEL_FONT;
    const half = ctx.measureText(wedge.label).width / 2 + SIDE_PAD;
    const spot = findWedgeSpot(wedge, view, paint.squash, half, space);
    if (spot === null) continue;
    ctx.fillStyle = (paint.palette[wedge.k] ?? '') + hex(LABEL_ALPHA * fade);
    ctx.fillText(wedge.label, spot.x, spot.y);
    ctx.font = COUNT_FONT;
    ctx.fillStyle = `rgba(150,164,192,${(COUNT_ALPHA * fade).toFixed(2)})`;
    ctx.fillText(paint.countLine({ held: wedge.held, alone: wedge.count }), spot.x, spot.y + COUNT_DROP);
    written += 1;
  }
  return written;
}
